import {usePlantStatus, type KpiDashboardLocationType} from '@domain/kpis'
import {usePlantStatusDateRange, PlantStatusPage} from '@features/performance-reports'
import {appUrl} from '@hconnect/common/hproduce/config'
import {COCKPIT_HOST} from '@hconnect/common/hproduce/config/cockpit'
import {EASY_POM_HOST} from '@hconnect/common/hproduce/config/easyPOM'
import {MAINTAIN_HOST} from '@hconnect/common/hproduce/config/maintain'
import {DASHBOARD_SEGMENT, SUMMARY_SEGMENT} from '@hconnect/common/hproduce/consts'
import {DateRange} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams, generatePath, useNavigate} from 'react-router'

import {PlantStatusPageReferer} from '../../shared/consts'
import {handleError} from '../../shared/helpers'
import {useNavAccessContext} from '../../shared/hooks/useNavAccessContext'
import {
  PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD,
  PLANT_PROCESS_ENGINEER_DASHBOARD,
  PLANT_RELIABILITY_ENGINEER_DASHBOARD
} from '../../shared/routes'
import {useTranslationPrefix} from '../hooks/useTranslationPrefix'
import {GLOBAL_KILN_STATUS, LOCATION_KPI, LOCATION_KPI_STATUS} from '../routing'

const getLocationTranslationKeys = function (
  prefix: string
): Record<KpiDashboardLocationType, string> {
  return {
    country: `${prefix}.goBackLabels.backToCountry`,
    area: `${prefix}.goBackLabels.backToArea`,
    region: `${prefix}.goBackLabels.backToRegion`,
    plant: `${prefix}.plantStatus.label.backToPlant`
  }
}

const RefererPathMap: Record<PlantStatusPageReferer, string> = {
  [PlantStatusPageReferer.STATUS]: LOCATION_KPI_STATUS,
  [PlantStatusPageReferer.PERFORMANCE]: LOCATION_KPI,
  [PlantStatusPageReferer.PROCESS_ENGINEER_DASHBOARD]: PLANT_PROCESS_ENGINEER_DASHBOARD,
  [PlantStatusPageReferer.RELIABILITY_ENGINEER_DASHBOARD]: PLANT_RELIABILITY_ENGINEER_DASHBOARD,
  [PlantStatusPageReferer.OPTIMIZATION_SPECIALIST_DASHBOARD]:
    PLANT_OPTIMIZATION_SPECIALIST_DASHBOARD
}

type PathParameter = {
  plantId: string
}

type Props = {
  referer?: PlantStatusPageReferer
}

export const PlantStatus: React.FC<Props> = ({referer = PlantStatusPageReferer.STATUS}) => {
  const {plantId} = useParams<PathParameter>()
  if (!plantId) throw new Error('Missing plantId prop')
  const navigate = useNavigate()
  const {isPlantAllowed, allowMultiplePlants, allowedLocations} = useNavAccessContext()

  useEffect(() => {
    if (!isPlantAllowed(plantId)) {
      void navigate(GLOBAL_KILN_STATUS)
    }
  }, [isPlantAllowed, navigate, plantId])

  const {t} = useTranslation()
  const {performancePrefix, hrocPrefix} = useTranslationPrefix()
  const timezone = useUpmPlantTimezone(plantId)
  const {from, to}: DateRange<Date> = usePlantStatusDateRange({timezone})
  const dateRangeIso = {from: from.toISOString(), to: to.toISOString()}
  const {data, error} = usePlantStatus(plantId, {
    startDate: dateRangeIso.from,
    endDate: dateRangeIso.to
  })

  useEffect(() => {
    if (error) {
      handleError(error, navigate)
    }
  }, [error, navigate])

  const locationTranslationKeys = useMemo(
    () => getLocationTranslationKeys(performancePrefix),
    [performancePrefix]
  )

  const easyPomLinkPath = `${appUrl(EASY_POM_HOST)}${generatePath(
    `/:plantId/${DASHBOARD_SEGMENT}`,
    {
      plantId
    }
  )}`
  const stoppageListHref = `${appUrl(MAINTAIN_HOST)}${generatePath(
    '/plant/:plantId/rcfa/stoppages',
    {
      plantId
    }
  )}`
  const shiftSummaryHref = `${appUrl(COCKPIT_HOST)}${generatePath(`/:plantId/${SUMMARY_SEGMENT}`, {
    plantId
  })}`

  const parentLocationIsAccessible =
    data?.plant.parentLocationId && allowedLocations.includes(data?.plant.parentLocationId)

  const showBackToParentButton = allowMultiplePlants() && parentLocationIsAccessible

  const getBackButtonProps = () => {
    const backButtonLocation = RefererPathMap[referer]

    switch (referer) {
      case PlantStatusPageReferer.PROCESS_ENGINEER_DASHBOARD:
        return {
          targetName: t(`${performancePrefix}.goBackLabels.backToTarget`, {
            target: t(`${hrocPrefix}.processEngineerDashboard.pageTitle`)
          }),
          target: generatePath(backButtonLocation, {
            plantId
          })
        }

      case PlantStatusPageReferer.RELIABILITY_ENGINEER_DASHBOARD:
        return {
          targetName: t(`${performancePrefix}.goBackLabels.backToTarget`, {
            target: t(`${hrocPrefix}.reliabilityEngineerDashboard.pageTitle`)
          }),
          target: generatePath(backButtonLocation, {
            plantId
          })
        }

      case PlantStatusPageReferer.OPTIMIZATION_SPECIALIST_DASHBOARD:
        return {
          targetName: t(`${performancePrefix}.goBackLabels.backToTarget`, {
            target: t(`${hrocPrefix}.optimizationSpecialistDashboard.pageTitle`)
          }),
          target: generatePath(backButtonLocation, {
            plantId
          })
        }

      default:
        if (showBackToParentButton) {
          return {
            targetName: data?.plant.parentLocationType
              ? t(locationTranslationKeys[data.plant.parentLocationType])
              : t(`${performancePrefix}.plantStatus.label.back`),
            target: generatePath(backButtonLocation, {
              locationId: data?.plant.parentLocationId || null
            })
          }
        }
    }
  }

  return (
    <PlantStatusPage
      plantId={plantId}
      easyPomLinkPath={easyPomLinkPath}
      stoppageListHref={stoppageListHref}
      shiftSummaryHref={shiftSummaryHref}
      backButtonProps={getBackButtonProps()}
      inverted
    />
  )
}
